<script setup lang="ts">
/**
 * POST /connectors. One dialog for every ConnectorType the contract knows — s3 · postgres ·
 * mysql · api — with the fields of that type's config and credentials. A connector belongs to
 * a project, so the project is chosen here. An S3-compatible store is picked by name and fills
 * the endpoint shape, region and addressing style; the values stay editable. Nothing is probed
 * before saving: the contract has no test operation; reachability shows on the first browse.
 */
import type { components } from '@scalty/contracts'
import Dialog from '~/components/ui/Dialog.vue'
import Choice from '~/components/ui/Choice.vue'
import Field from '~/components/ui/Field.vue'
import SelectField from '~/components/ui/SelectField.vue'
import Segmented from '~/components/ui/Segmented.vue'
import Toggle from '~/components/ui/Toggle.vue'
import Icon from '~/components/ui/Icon.vue'
import ActionButton from '~/components/ui/ActionButton.vue'

type S = components['schemas']
type Kind = S['ConnectorType']
const props = defineProps<{ projects: { value: string; label: string }[]; projectId: string }>()
const emit = defineEmits<{ close: []; submit: [payload: S['CreateConnectorRequest']] }>()

const kinds: { id: Kind; title: string; facts: string }[] = [
  { id: 's3', title: 'S3-compatible store', facts: 'Amazon S3 · R2 · MinIO · B2 · Spaces · access key' },
  { id: 'postgres', title: 'Postgres', facts: 'read-only connection · one table per dataset' },
  { id: 'mysql', title: 'MySQL', facts: 'read-only connection · one table per dataset' },
  { id: 'api', title: 'HTTP API', facts: 'GET endpoint · JSON records · header auth' },
]
/* The stores whose S3 address shape is known. A placeholder in angle brackets is the part the user fills in. */
const stores = [
  { value: 'aws', label: 'Amazon S3', endpoint: '', region: 'eu-central-1', pathStyle: false },
  { value: 'r2', label: 'Cloudflare R2', endpoint: 'https://<account id>.r2.cloudflarestorage.com', region: 'auto', pathStyle: true },
  { value: 'minio', label: 'MinIO', endpoint: 'https://minio.example.com', region: 'us-east-1', pathStyle: true },
  { value: 'b2', label: 'Backblaze B2', endpoint: 'https://s3.<region>.backblazeb2.com', region: 'eu-central-003', pathStyle: false },
  { value: 'spaces', label: 'DigitalOcean Spaces', endpoint: 'https://<region>.digitaloceanspaces.com', region: 'fra1', pathStyle: false },
  { value: 'yandex', label: 'Yandex Object Storage', endpoint: 'https://storage.yandexcloud.net', region: 'ru-central1', pathStyle: false },
  { value: 'other', label: 'Other S3-compatible store', endpoint: 'https://s3.example.com', region: 'us-east-1', pathStyle: true },
]
const kind = ref<Kind>('s3')
const store = ref('aws')
const preset = computed(() => stores.find((entry) => entry.value === store.value) ?? stores[0]!)
const project = ref(props.projectId || props.projects[0]?.value || '')
const name = ref('')
const f = reactive({
  bucket: '', region: 'eu-central-1', prefix: '', endpointUrl: '', pathStyle: false, accessKeyId: '', secretAccessKey: '',
  host: '', port: '', database: '', schema: 'public', user: '', password: '',
  baseUrl: '', authHeader: 'Authorization', authHeaderValue: '', recordsPath: '', maxPages: '10',
  strategy: 'none', cursorParam: 'cursor', nextCursorPath: '', pageParam: 'page', startPage: '1', pageSizeParam: '', pageSize: '',
})
watch(store, () => { f.region = preset.value.region; f.pathStyle = preset.value.pathStyle; f.endpointUrl = preset.value.endpoint.includes('<') ? '' : preset.value.endpoint })
const regions = ['eu-central-1', 'eu-west-1', 'eu-north-1', 'us-east-1', 'us-west-2', 'ap-southeast-1'].map((r) => ({ value: r, label: r }))
const integer = (value: string, fallback?: number) => { const n = Number.parseInt(value, 10); return Number.isFinite(n) ? n : fallback }

const complete = computed(() => {
  if (!name.value.trim() || !project.value) return false
  if (kind.value === 's3') return Boolean(f.bucket && f.region && f.accessKeyId && f.secretAccessKey && (store.value === 'aws' || f.endpointUrl))
  if (kind.value === 'postgres' || kind.value === 'mysql') return Boolean(f.host && f.database && f.user && f.password)
  const paging = f.strategy === 'none' || (f.strategy === 'cursor' ? Boolean(f.cursorParam && f.nextCursorPath) : Boolean(f.pageParam && integer(f.startPage) !== undefined))
  return Boolean(f.baseUrl && f.authHeader && f.authHeaderValue && f.recordsPath && integer(f.maxPages) && paging)
})

function payload(): S['CreateConnectorRequest'] {
  const base = { projectId: project.value, type: kind.value, name: name.value.trim() }
  if (kind.value === 's3') {
    const endpointUrl = store.value === 'aws' ? '' : f.endpointUrl.trim()
    return { ...base, config: { bucket: f.bucket.trim(), region: f.region.trim(), ...(f.prefix ? { prefix: f.prefix } : {}), ...(endpointUrl ? { endpointUrl } : {}), pathStyle: endpointUrl ? f.pathStyle : false }, credentials: { accessKeyId: f.accessKeyId, secretAccessKey: f.secretAccessKey } }
  }
  if (kind.value === 'postgres') return { ...base, config: { host: f.host, database: f.database, port: integer(f.port, 5432) ?? 5432, schema: f.schema || 'public' }, credentials: { user: f.user, password: f.password } }
  if (kind.value === 'mysql') return { ...base, config: { host: f.host, database: f.database, port: integer(f.port, 3306) ?? 3306 }, credentials: { user: f.user, password: f.password } }
  const pagination: S['ApiPagination'] = f.strategy === 'cursor'
    ? { strategy: 'cursor', cursorParam: f.cursorParam, nextCursorPath: f.nextCursorPath }
    : f.strategy === 'page'
      ? { strategy: 'page', pageParam: f.pageParam, startPage: integer(f.startPage, 1) ?? 1, ...(f.pageSizeParam ? { pageSizeParam: f.pageSizeParam } : {}), ...(integer(f.pageSize) ? { pageSize: integer(f.pageSize) } : {}) }
      : { strategy: 'none' }
  return { ...base, config: { baseUrl: f.baseUrl, authHeader: f.authHeader, recordsPath: f.recordsPath, pagination, maxPages: integer(f.maxPages, 10) ?? 10 }, credentials: { authHeaderValue: f.authHeaderValue } }
}
</script>

<template>
  <Dialog title="New connector" meta="a source in one project · used by datasets" :width="560" @close="emit('close')">
    <template #icon><Icon name="connector" /></template>

    <div class="grid grid-cols-2 gap-1.5">
      <Choice v-for="k in kinds" :key="k.id" :title="k.title" :facts="k.facts" :selected="kind === k.id" @click="kind = k.id" />
    </div>

    <div class="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <Field v-model="name" label="Name" placeholder="support-archive" hint="Shown on datasets that read from it" required />
      <SelectField v-model="project" label="Project" :options="projects" hint="Datasets from this source land in it" />
    </div>

    <template v-if="kind === 's3'">
      <div class="grid grid-cols-[minmax(0,1fr)_var(--connector-port-width)] gap-3">
        <SelectField v-model="store" label="Store" :options="stores" />
        <SelectField v-if="store === 'aws'" v-model="f.region" label="Region" :options="regions" mono />
        <Field v-else v-model="f.region" label="Region" :placeholder="preset.region" required />
      </div>
      <div class="grid grid-cols-2 gap-3">
        <Field v-model="f.bucket" label="Bucket" placeholder="acme-support" required />
        <Field v-model="f.prefix" label="Prefix" placeholder="tickets/2024/" hint="Only objects under this path are listed" />
      </div>
      <template v-if="store !== 'aws'">
        <Field v-model="f.endpointUrl" label="Endpoint" :placeholder="preset.endpoint" :hint="preset.endpoint.includes('<') ? 'Fill in the part in angle brackets' : 'The S3 address of the store'" required />
        <div class="rounded-md border border-border px-2.5 py-2"><Toggle v-model="f.pathStyle" label="Path-style addressing" facts="bucket in the path, not the host" /></div>
      </template>
      <div class="grid grid-cols-2 gap-3">
        <Field v-model="f.accessKeyId" label="Access key id" autocomplete="off" required />
        <Field v-model="f.secretAccessKey" label="Secret access key" type="password" autocomplete="off" required />
      </div>
    </template>

    <template v-else-if="kind === 'postgres' || kind === 'mysql'">
      <div class="grid grid-cols-[minmax(0,1fr)_var(--connector-port-width)] gap-3">
        <Field v-model="f.host" label="Host" placeholder="db.internal" required />
        <Field v-model="f.port" label="Port" type="number" :placeholder="kind === 'postgres' ? '5432' : '3306'" />
      </div>
      <div class="grid grid-cols-2 gap-3">
        <Field v-model="f.database" label="Database" placeholder="billing" required />
        <Field v-if="kind === 'postgres'" v-model="f.schema" label="Schema" placeholder="public" />
      </div>
      <div class="grid grid-cols-2 gap-3">
        <Field v-model="f.user" label="User" hint="A read-only role is enough" required />
        <Field v-model="f.password" label="Password" type="password" autocomplete="off" required />
      </div>
    </template>

    <template v-else>
      <Field v-model="f.baseUrl" label="Endpoint" placeholder="https://api.acme.com/v1/tickets" hint="GET · JSON" required />
      <div class="grid grid-cols-[var(--connector-auth-width)_minmax(0,1fr)] gap-3">
        <Field v-model="f.authHeader" label="Auth header" required />
        <Field v-model="f.authHeaderValue" label="Header value" type="password" autocomplete="off" hint="Sent as the header's value, for example Bearer …" required />
      </div>
      <div class="grid grid-cols-[minmax(0,1fr)_var(--connector-port-width)] gap-3">
        <Field v-model="f.recordsPath" label="Records path" placeholder="data.items" hint="Where the record array sits in each response" required />
        <Field v-model="f.maxPages" label="Max pages" type="number" required />
      </div>
      <Segmented v-model="f.strategy" label="Pagination" :options="[{ value: 'none', label: 'Single request' }, { value: 'cursor', label: 'Cursor' }, { value: 'page', label: 'Page number' }]" />
      <div v-if="f.strategy === 'cursor'" class="grid grid-cols-2 gap-3">
        <Field v-model="f.cursorParam" label="Cursor parameter" required />
        <Field v-model="f.nextCursorPath" label="Next cursor path" placeholder="meta.next" required />
      </div>
      <div v-else-if="f.strategy === 'page'" class="grid grid-cols-2 gap-3">
        <Field v-model="f.pageParam" label="Page parameter" required />
        <Field v-model="f.startPage" label="First page" type="number" required />
        <Field v-model="f.pageSizeParam" label="Page size parameter" />
        <Field v-model="f.pageSize" label="Page size" type="number" />
      </div>
    </template>

    <template #actions>
      <ActionButton label="Cancel" @click="emit('close')" />
      <ActionButton label="Save connector" variant="primary" :disabled="!complete" @click="emit('submit', payload())"><template #icon><Icon name="check" /></template></ActionButton>
    </template>
  </Dialog>
</template>
